import { useMemo } from "react";
import type { ColumnDef } from "../../components/base-table-layout";
import { createEhrWriteBackColumn } from "../../ehr/create-ehr-column";
import { EhrContextValue } from "../../../shared-logic/ehr-context/ehr-context";
import { createImagingReportFilters } from "../../utils/imaging-report-filters";
import { createNoteMatchData } from "../../utils/note-processing";
import { TableRow } from "../../utils/tables-metadata";
import { ImagingReportRowData } from "./imaging-reports-rows";

/**
 * Creates column definitions for imaging reports tables.
 * Optionally includes EHR write-back column if context is available.
 */
export function useImagingColumns(ehrContext: EhrContextValue) {
  return useMemo(() => {
    const cols: ColumnDef<ImagingReportRowData>[] = [
      { key: "imagingDisplay", header: "Imaging Display", maxWidth: "300px" },
      {
        key: "imagingModality",
        header: "Imaging Modality",
        maxWidth: "200px",
        hideOnSmallScreen: true,
      },
      { key: "bodyRegion", header: "Body Region", hideOnSmallScreen: true },
      { key: "location", header: "Location", hideOnSmallScreen: true },
      { key: "date", header: "Date", sort: "desc" },
    ];

    const ehrColumn = createEhrWriteBackColumn<ImagingReportRowData>(ehrContext, "note");
    if (ehrColumn) cols.push(ehrColumn);

    return cols;
  }, [ehrContext]);
}

export type ImagingFilterOption = {
  label: string;
  groupLabel: string;
  isChecked: boolean;
  filterFn: (rowData: ImagingReportRowData) => boolean;
};

/**
 * Creates filter options for imaging reports tables based on row data.
 * Filters by imaging modality and body region.
 */
export function useImagingFilterOptions(rows: TableRow[]): ImagingFilterOption[] {
  return useMemo(() => {
    const imagingRows = rows as ImagingReportRowData[];
    const filters = createImagingReportFilters(imagingRows);

    return filters.map(option => ({
      label: option.label,
      groupLabel: option.groupLabel,
      isChecked: true,
      filterFn: (rowData: ImagingReportRowData) => {
        const parts = option.value.split(":");
        const type = parts[0];
        const value = parts[1];
        if (!type || !value) return true;

        switch (type) {
          case "imagingModality":
            if (value === "undefined") {
              return !rowData.imagingModality || rowData.imagingModality === "-";
            }
            if (!rowData.imagingModality || rowData.imagingModality === "-") return false;
            return rowData.imagingModality
              .split(",")
              .map(v => v.trim())
              .includes(value);
          case "bodyRegion":
            if (value === "undefined") {
              return !rowData.bodyRegion || rowData.bodyRegion === "-";
            }
            if (!rowData.bodyRegion || rowData.bodyRegion === "-") return false;
            return rowData.bodyRegion
              .split(",")
              .map(v => v.trim())
              .includes(value);
          default:
            return true;
        }
      },
    }));
  }, [rows]);
}

/**
 * Checks if a row has note content matches for the given search query.
 */
export function hasImagingNoteMatches(row: TableRow, filterQuery: string): boolean {
  const noteData = row.noteData as { fullText: string } | undefined;
  if (!noteData?.fullText || !filterQuery.trim()) return false;
  const matchData = createNoteMatchData(noteData.fullText, filterQuery);
  return Boolean(matchData && matchData.count > 0);
}
